import {
  createSlice,
  isPending,
  isRejected,
  PayloadAction,
} from '@reduxjs/toolkit';

import { RootState } from '../..';
import { ISection, TSectionStatus } from '../SectionSlice';
import { IMeetingRequest } from '../MeetingRequestsSlice';
import {
  adminGetMeetings,
  getScheduleByIdAsAdmin,
  getUserSchedulesAsAdmin,
} from './adminApi';
import { getScheduleByIdAsUser, getUserSchedulesAsUser } from './api';
import { ICourseTutor } from '../CourseTutorsSlice';
import { ICourseStudent } from '../CourseStudentsSlice';
import { IUser, TIdentifiedUserRole } from '../../UserSlice';

export type IScheduleType = '1-1' | 'GROUP';

export interface IScheduleShortEvent {
  _id: string;
  type: IScheduleType;
  title: string;
  course: Record<string, unknown>;
  status: TSectionStatus;
  start: string;
  end: string;
  duration: number;
  subject: string;
  curriculum: string;
  grade: string;
  waitingId?: string;
  zoomRequest?: IMeetingRequest;
  tutors: ICourseTutor[];
  sectionType: ISection['type'];
  students?: ICourseStudent[];
}

export interface IScheduleFullEvent extends IScheduleShortEvent {
  identifiedUserRole?: TIdentifiedUserRole;
  tutorWaitingId: string;
  studentWaitingId: string;
  tutors: ICourseTutor[];
  students: ICourseStudent[];
}

export interface IAdminUserScheduleData {
  list: IScheduleShortEvent[];
}

export interface IUserScheduleData {
  list: IScheduleShortEvent[];
}

export const SCHEDULE_TYPES = {
  ONE_TO_ONE: '1 on 1',
  GROUP: 'GROUP',
  TRIAL: 'TRIAL',
};

export interface IInitialScheduleState {
  status: 'idle' | 'loading' | 'rejected';
  userScheduleData?: IUserScheduleData;
  userScheduleEvent?: IScheduleFullEvent;
  // ADMIN
  adminUserScheduleData?: IAdminUserScheduleData;
  adminScheduleEvent?: IScheduleFullEvent;
  adminMeetingsData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ISection & { students: IUser[] }>;
    totalDuration?: number;
  };
}

const initialState: IInitialScheduleState = {
  status: 'idle',
  userScheduleData: undefined,
  userScheduleEvent: undefined,
  // ADMIN
  adminUserScheduleData: undefined,
  adminScheduleEvent: undefined,
  adminMeetingsData: undefined,
};

export const ScheduleSlice = createSlice({
  initialState,
  name: 'schedule',
  reducers: {
    resetUserScheduleData: (state) => {
      state.userScheduleData = undefined;
    },
    resetUserScheduleEvent: (state) => {
      state.userScheduleEvent = undefined;
    },
    updateScheduleEvent: (
      state,
      action: PayloadAction<Partial<IScheduleFullEvent>>
    ) => {
      if (state.userScheduleEvent) {
        const newUserScheduleEvent = {
          ...state.userScheduleEvent,
          ...action.payload,
        };

        state.userScheduleEvent = newUserScheduleEvent;
      }
    },
    removeUserScheduleEventFromList: (state, action: PayloadAction<string>) => {
      if (
        state.userScheduleData &&
        state.userScheduleData.list &&
        state.userScheduleData.list.length
      ) {
        const newUserScheduleData = { ...state.userScheduleData };

        newUserScheduleData.list = [...newUserScheduleData.list].filter(
          (item) => item._id !== action.payload
        );

        state.userScheduleData = newUserScheduleData;
      }
    },
    updateOneUserScheduleItem: (
      state,
      action: PayloadAction<{
        id: string;
        attributes: Partial<IScheduleShortEvent>;
      }>
    ) => {
      if (
        state.userScheduleData &&
        state.userScheduleData.list &&
        state.userScheduleData.list.length
      ) {
        const { payload } = action;
        const newUserScheduleData = { ...state.userScheduleData };

        const index = newUserScheduleData.list.findIndex(
          (item) => item._id === payload.id
        );

        if (index > -1) {
          const matchedItem = {
            ...newUserScheduleData.list[index],
            ...payload.attributes,
          };

          newUserScheduleData.list[index] = matchedItem;

          state.userScheduleData = newUserScheduleData;
        }
      }
    },
    // ADMIN
    resetAdminUserScheduleData: (state) => {
      state.adminUserScheduleData = undefined;
    },
    resetAdminScheduleEvent: (state) => {
      state.adminScheduleEvent = undefined;
    },
    updateAdminScheduleEvent: (
      state,
      action: PayloadAction<Partial<IScheduleFullEvent>>
    ) => {
      if (state.adminScheduleEvent) {
        const newAdminScheduleEvent = {
          ...state.adminScheduleEvent,
          ...action.payload,
        };

        state.adminScheduleEvent = newAdminScheduleEvent;
      }
    },
    removeAdminScheduleEventFromList: (
      state,
      action: PayloadAction<string>
    ) => {
      if (
        state.adminUserScheduleData &&
        state.adminUserScheduleData.list &&
        state.adminUserScheduleData.list.length
      ) {
        const newAdminUserScheduleData = { ...state.adminUserScheduleData };

        newAdminUserScheduleData.list = [
          ...newAdminUserScheduleData.list,
        ].filter((item) => item._id !== action.payload);

        state.adminUserScheduleData = newAdminUserScheduleData;
      }
    },
    updateOneAdminUserScheduleItem: (
      state,
      action: PayloadAction<{
        id: string;
        attributes: Partial<IScheduleShortEvent>;
      }>
    ) => {
      if (
        state.adminUserScheduleData &&
        state.adminUserScheduleData.list &&
        state.adminUserScheduleData.list.length
      ) {
        const { payload } = action;
        const newAdminUserScheduleData = { ...state.adminUserScheduleData };

        const index = newAdminUserScheduleData.list.findIndex(
          (item) => item._id === payload.id
        );

        if (index > -1) {
          const matchedItem = {
            ...newAdminUserScheduleData.list[index],
            ...payload.attributes,
          };

          newAdminUserScheduleData.list[index] = matchedItem;

          state.adminUserScheduleData = newAdminUserScheduleData;
        }
      }
    },
    resetAdminMeetingsData: (state) => {
      state.adminMeetingsData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserSchedulesAsUser.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        state.userScheduleData = result;
      })
      .addCase(getScheduleByIdAsUser.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        state.userScheduleEvent = result;
      });

    // ADMIN
    builder
      .addCase(getUserSchedulesAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        state.adminUserScheduleData = result;
      })
      .addCase(getScheduleByIdAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        state.adminScheduleEvent = result;
      })
      .addCase(adminGetMeetings.fulfilled, (state, action) => {
        state.status = 'idle';
        const result = action.payload;

        const newAdminMeetingsData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
          totalDuration: result.totalDuration,
        };

        state.adminMeetingsData = newAdminMeetingsData;
      });

    builder
      .addMatcher(
        isPending(
          getUserSchedulesAsAdmin,
          getScheduleByIdAsAdmin,
          getUserSchedulesAsUser,
          getScheduleByIdAsUser,
          adminGetMeetings
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          getUserSchedulesAsAdmin,
          getScheduleByIdAsAdmin,
          getUserSchedulesAsUser,
          getScheduleByIdAsUser,
          adminGetMeetings
        ),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const getScheduleState = (state: RootState): RootState['schedule'] =>
  state.schedule;

export const {
  resetAdminUserScheduleData,
  resetAdminScheduleEvent,
  updateAdminScheduleEvent,
  removeAdminScheduleEventFromList,
  resetUserScheduleData,
  resetUserScheduleEvent,
  updateScheduleEvent,
  removeUserScheduleEventFromList,
  updateOneUserScheduleItem,
  updateOneAdminUserScheduleItem,
  resetAdminMeetingsData,
} = ScheduleSlice.actions;

export * from './adminApi';
export * from './api';
export default ScheduleSlice.reducer;
